import React from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { ChartDatapointHoverAnnotation } from 'features/charts-v2/components/ChartDatapointHoverAnnotation'
import { barMinPointSize } from 'features/charts-v2/constants'
import { chartConfig } from 'features/charts-v2/services/chart-config-service'
import { dataTransformationService } from 'features/charts-v2/services/data-transformation-service'
import { BaseChartStrategy } from 'features/charts-v2/strategies/Base'
import { METRICS, type MetricType } from 'features/charts-v2/types'
import {
  ChartProps,
  TransformedChartData,
} from 'features/charts-v2/types/chart-strategy'
import { ChartTypeV2 } from 'features/charts-v2/types/chart-types'
import {
  calculateAvailableXAxisWidth,
  formatAxisNumber,
  OVERFLOW_CONFIG,
} from 'features/charts-v2/utils/axis-tick-utils'
import { CustomXAxisTick } from 'features/charts-v2/utils/AxisTickUtils'
import { getMetricLabel } from 'features/charts-v2/utils/metric-format-utils'

/**
 * Format number for display in labels
 * - Whole numbers: no decimals (e.g., 5)
 * - Decimals: max 2 decimal places (e.g., 4.91, 3.5)
 */
const formatLabelValue = (value: number): string => {
  if (Number.isInteger(value)) {
    return value.toString()
  }
  // Round to 2 decimal places and remove trailing zeros
  return parseFloat(value.toFixed(2)).toString()
}

/**
 * Custom label component for histogram bar data points
 * Displays the value above each bar
 */
const CustomHistogramBarLabel = React.memo<{
  x?: number
  y?: number
  width?: number
  height?: number
  value?: number
  fontSizePx?: number
}>(({ x = 0, y = 0, width = 0, value, fontSizePx = 13 }) => {
  // Don't render label if value is null/undefined/0
  if (value == null || value === 0) return null

  return (
    <text
      x={x + width / 2}
      y={y - 5}
      fill="#333"
      fontSize={fontSizePx}
      textAnchor="middle"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      {formatLabelValue(value)}
    </text>
  )
})

CustomHistogramBarLabel.displayName = 'CustomHistogramBarLabel'

// Recharts bar chart event types
interface RechartsHistogramMouseEvent {
  activeLabel?: string
}

// Histogram data point type
interface HistogramDataPoint {
  name: string
  [category: string]: string | number
}

// Bar click data type
interface BarClickData {
  payload?: HistogramDataPoint
}

export class HistogramChartStrategy extends BaseChartStrategy {
  public readonly chartType = ChartTypeV2.HISTOGRAM
  public readonly requiredMetrics: MetricType[] = [METRICS.RECORD_COUNT.id]
  public readonly optionalMetrics: MetricType[] = [
    METRICS.SENTENCE_COUNT.id,
    METRICS.SENTIMENT.id,
  ]

  public transform(props: ChartProps): TransformedChartData {
    const breakdowns = props.selectedBreakdowns || []

    // Handle 0 breakdowns: show single histogram bar with total
    if (breakdowns.length === 0) {
      return dataTransformationService.transformForTotalOnly(
        props.data!,
        props.selectedMetrics,
      )
    }

    return dataTransformationService.transformForHistogram(
      props.data!,
      props.selectedMetrics,
    )
  }

  public render(
    props: ChartProps & { transformedData: TransformedChartData },
  ): React.ReactElement {
    const validation = this.validate(props)
    if (!validation.isValid) {
      return this.renderValidationError(validation)
    }

    const {
      transformedData,
      onDataPointHover,
      selectedMetrics,
      onSegmentClick,
      fontSizePx = 13,
      showXAxisLabel = true,
      showYAxisLabel = true,
      showDatapointLabels = true,
      xAxisOverflow = 'expand',
      disableAnimation = false,
    } = props

    if (!transformedData.chartData || transformedData.chartData.length === 0) {
      return this.renderEmptyState(
        'No data available',
        'Try adding some filters.',
      )
    }

    const breakdownCategories =
      transformedData.metadata?.breakdownCategories || []

    // Calculate dynamic maxWidth for wrap mode based on data length
    // Estimate: assume chart is ~1200px wide (typical for full-width charts)
    const estimatedChartWidth = 1200
    const maxWidthPerLabel =
      xAxisOverflow === 'wrap'
        ? calculateAvailableXAxisWidth(
            estimatedChartWidth,
            transformedData.chartData.length,
            { left: 40, right: 30 },
          )
        : 100

    const tickRenderer = (props: {
      x: number
      y: number
      payload: { value: string }
      [k: string]: unknown
    }): JSX.Element => (
      <CustomXAxisTick
        {...props}
        fontSizePx={fontSizePx}
        overflowMode={xAxisOverflow}
        maxWidth={maxWidthPerLabel}
      />
    )

    // Get configuration for the current overflow mode
    const overflowConfig = OVERFLOW_CONFIG[xAxisOverflow]
    const xAxisHeight = overflowConfig.height
    const bottomMargin = overflowConfig.margin

    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={transformedData.chartData}
          margin={{
            top: 20,
            right: 30,
            bottom: bottomMargin,
            left: 20,
          }}
          barCategoryGap={10}
          onMouseMove={(data: RechartsHistogramMouseEvent) => {
            if (data && data.activeLabel) {
              onDataPointHover?.(data.activeLabel)
            }
          }}
          onMouseLeave={() => onDataPointHover?.(null)}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            height={xAxisHeight}
            interval={0}
            tick={showXAxisLabel ? tickRenderer : false}
          />
          <YAxis
            tick={{ fontSize: fontSizePx }}
            tickFormatter={formatAxisNumber}
            label={
              showYAxisLabel
                ? {
                    value: this.getMetricLabel([selectedMetrics[0]!]),
                    angle: -90,
                    position: 'insideLeft',
                    style: {
                      textAnchor: 'middle',
                      fill: '#666',
                      fontSize: fontSizePx,
                    },
                  }
                : undefined
            }
          />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null
              const metricLabel =
                selectedMetrics.length === 1
                  ? getMetricLabel(selectedMetrics[0]!)
                  : 'Count'
              const rows = payload.map((entry) => {
                const val = entry.value as number
                const formattedValue = selectedMetrics.includes(
                  METRICS.SENTIMENT.id,
                )
                  ? val.toFixed(2)
                  : val.toLocaleString()
                return {
                  label:
                    payload.length > 1
                      ? `${entry.name} ${metricLabel}`
                      : metricLabel,
                  value: formattedValue,
                  percentage: null,
                  color: null,
                }
              })
              return (
                <ChartDatapointHoverAnnotation
                  title={String(label)}
                  rows={rows}
                  footer={null}
                  focusedField={null}
                />
              )
            }}
          />
          {/* Create a Bar component for each breakdown category */}
          {breakdownCategories.map((category, index) => (
            <Bar
              key={category}
              dataKey={category}
              name={category}
              fill={chartConfig.getColor('histogram', index)}
              radius={[2, 2, 0, 0]}
              minPointSize={barMinPointSize}
              isAnimationActive={!showDatapointLabels && !disableAnimation}
              label={
                showDatapointLabels
                  ? (props: Record<string, unknown>) => (
                      <CustomHistogramBarLabel
                        {...props}
                        fontSizePx={fontSizePx}
                      />
                    )
                  : false
              }
              onClick={(
                data: BarClickData,
                _index: number,
                event: React.MouseEvent,
              ) => {
                if (onSegmentClick && data && data.payload) {
                  onSegmentClick(
                    {
                      primaryBreakdown: data.payload.name,
                      stackingBreakdown: category,
                      value: (data.payload[category] as number) || 0,
                      payload: data.payload,
                    },
                    event,
                  )
                }
              }}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    )
  }

  public getTitle(): string {
    return 'Distribution Histogram (Demo)'
  }
}
